import type { ReactNode } from 'react'
import { useLingui } from '@lingui/react/macro'
import { XIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ButtonSkeleton, DialogSkeleton, TextSkeleton } from '@/components/custom/dialog-skeleton'
import { useModalSlotPending } from '@/components/custom/modal-slot'

/**
 * The describe wizard's frame, shared by the dialog (describe-profile-flow.tsx) and its pending skeleton, so the two
 * cannot drift apart: the pending paints the same header, grid and footer boxes before the wizard's chunk has
 * arrived, and the wizard fills them in place. Kept light on purpose, since the route's pendingComponent is eager.
 */

export type Indicator = 'competences' | 'values' | 'personality'
export type Step = 'relevant' | 'irrelevant' | 'adjust'

export const STEP_ORDER: Step[] = ['relevant', 'irrelevant', 'adjust']
export const INDICATOR_LENGTH = 18
/** The indicators come in three dimensions (columns) of six. */
const COLUMNS = 3
const ROWS = INDICATOR_LENGTH / COLUMNS

/**
 * Full-screen on a phone, a centred card from `sm`. No height cap and no clip: the viewport scrolls the wizard, and
 * its header and footer stick to the viewport's edges (DescribeLayout).
 */
export const DESCRIBE_DIALOG_CLASS =
  'flex min-h-dvh w-full max-w-[100vw]! flex-col gap-0 rounded-none p-0 sm:min-h-0 sm:max-w-200! sm:rounded-xl'
/** A phone's full-screen wizard keeps no gutter above and below. */
export const DESCRIBE_FRAME_CLASS = 'max-sm:py-0'
/** The close button, in the sticky header so it stays with it: the dialog's own would scroll away. */
export const DESCRIBE_CLOSE_CLASS = 'absolute top-2 right-2 z-20'
export const DESCRIBE_FULL_TITLE_CLASS = 'min-w-0 pr-10 text-lg leading-tight sm:text-xl'
export const DESCRIBE_SINGLE_TITLE_CLASS = 'min-w-0 text-lg leading-tight sm:text-xl'
export const DESCRIBE_STEP_TITLE_CLASS = 'text-foreground min-w-0 text-base leading-tight font-semibold sm:text-lg'

/** The stepper's colour per indicator: literal classes, so Tailwind's scanner keeps them. */
export const INDICATOR_STEP_COLOR = {
  competences: { name: 'amber', current: 'bg-amber-500' },
  values: { name: 'rose', current: 'bg-rose-500' },
  personality: { name: 'violet', current: 'bg-violet-500' },
} as const

/** The wizard's static copy: step titles, instructions, the reference notes and the footer's labels. */
export function useDescribeCopy() {
  const { t } = useLingui()
  const stepTitles: Record<Indicator, Record<Step, string>> = {
    competences: {
      relevant: t`Relevant competencies`,
      irrelevant: t`Non-relevant competencies`,
      adjust: t`Adjust competencies`,
    },
    values: {
      relevant: t`Relevant values`,
      irrelevant: t`Non-relevant values`,
      adjust: t`Adjust values`,
    },
    personality: {
      relevant: t`Relevant traits`,
      irrelevant: t`Non-relevant traits`,
      adjust: t`Adjust traits`,
    },
  }
  const sectionLabel: Record<Indicator, string> = {
    competences: t`competencies`,
    values: t`values`,
    personality: t`traits`,
  }
  const stepDescriptions: Record<Indicator, Record<'relevant' | 'irrelevant', string>> = {
    competences: {
      relevant: t`Select between three and six relevant competencies for this position.`,
      irrelevant: t`Select between three and six non-relevant competencies for this position.`,
    },
    values: {
      relevant: t`Select between three and six relevant values for this position.`,
      irrelevant: t`Select between three and six non-relevant values for this position.`,
    },
    personality: {
      relevant: t`Select between three and six relevant traits for this position.`,
      irrelevant: t`Select between three and six non-relevant traits for this position.`,
    },
  }
  /** Describe or Modify: which one depends on the job, so it is only known once the job is. */
  const singleTitle = (indicator: Indicator, step: Step, custom: boolean) => {
    const sectionForStep = sectionLabel[indicator]
    if (step === 'adjust') return t`Adjust ${sectionForStep}`
    if (step === 'relevant')
      return custom ? t`Modify relevant ${sectionForStep}` : t`Describe relevant ${sectionForStep}`
    return custom ? t`Modify non-relevant ${sectionForStep}` : t`Describe non-relevant ${sectionForStep}`
  }
  return {
    fullTitle: t`Required psychographic profile`,
    stepTitles,
    stepDescriptions,
    adjustDescription: t`Modify, if you consider it appropriate, the scores provided by the system.`,
    marketReference: t`We show as a reference the standard market indicators.`,
    lastSelectedReference: t`We show as a reference the last selected indicators.`,
    continueToIrrelevant: t`Continue to non-relevant`,
    singleTitle,
  }
}

/**
 * The frame: a sticky header (title, stepper, step heading, the job line, the instructions), then its body. Every
 * header box whose content can arrive late reserves its height (`min-h-[1lh]`, `min-h-[2lh]`), so nothing moves
 * when it does.
 */
export function DescribeLayout(props: {
  /** The close button (`DESCRIBE_CLOSE_CLASS`). */
  close: ReactNode
  /** The full flow's title above the stepper; absent for a single indicator. */
  title?: ReactNode
  steps: ReactNode
  /** The step heading (full flow) or the dialog title (single indicator). */
  heading: ReactNode
  jobLine: ReactNode
  description: ReactNode
  /** A `DescribeBody`, or a boundary around one. */
  children: ReactNode
}) {
  return (
    <div className="flex flex-1 flex-col">
      <div
        data-slot="dialog-header"
        className="bg-background sticky top-0 z-10 flex flex-col gap-4 px-4 pt-5 pb-5 sm:rounded-t-xl sm:px-6 sm:pt-6"
      >
        {props.close}
        {props.title}
        {props.steps}
        <div className="flex min-w-0 flex-col gap-1 pr-10">
          {props.heading}
          {/* A <div>, not a <p>: while the job loads, the line holds the ui Skeleton's <div>. */}
          <div
            data-slot="describe-job-line"
            className="text-muted-foreground min-h-[1lh] min-w-0 truncate text-xs sm:text-sm"
          >
            {props.jobLine}
          </div>
        </div>
        <p className="text-muted-foreground min-h-[2lh] text-sm leading-relaxed whitespace-pre-wrap">
          {props.description}
        </p>
      </div>
      {props.children}
    </div>
  )
}

/** The grid, then the sticky footer: one unit, since the footer's count and actions read the grid's selections. */
export function DescribeBody(props: { grid: ReactNode; footer: ReactNode }) {
  return (
    <>
      <div data-slot="describe-grid" className="px-4 pt-4 pb-4 sm:px-6">
        {props.grid}
      </div>
      <div className="bg-background sticky bottom-0 z-10 mt-auto sm:rounded-b-xl">{props.footer}</div>
    </>
  )
}

/** Steps' own box (shared/steps.tsx): one bar per step, the current one in the indicator's colour. */
function StepsSkeleton(props: { count: number; indicator: Indicator; full: boolean }) {
  return (
    <div className={cn('flex w-full max-w-sm items-center gap-1.5', props.full ? 'w-60 sm:w-72' : 'w-20 sm:w-24')}>
      {Array.from({ length: props.count }, (_step, index) => (
        <div
          key={index}
          className={cn(
            'h-1.5 flex-1 rounded-full',
            index === 0 ? INDICATOR_STEP_COLOR[props.indicator].current : 'bg-muted',
          )}
        />
      ))}
    </div>
  )
}

/** One indicator row's box, as DescribeProfile draws it: what fills it (its name, its state) is not known yet. */
function IndicatorRowSkeleton() {
  return (
    <li className="border-muted flex w-full items-center gap-2.5 rounded-lg border px-3 py-2">
      <Skeleton className="size-5 shrink-0 rounded-sm" />
      <Skeleton className="h-4 w-24" />
      {/* The info trigger's box (size-7), which DescribeProfile hides below `sm`. */}
      <span className="ml-auto size-7 shrink-0 max-sm:hidden" />
    </li>
  )
}

/**
 * The grid: three dimensions of six indicators, a column each from `sm`, stacked on a phone. `dimensions` draws each
 * column's header, which the public group survey's grid has none of.
 */
export function DescribeGridSkeleton(props: { dimensions?: boolean }) {
  return (
    <section className="flex w-full flex-col items-center gap-4">
      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-start sm:gap-3">
        {Array.from({ length: COLUMNS }, (_column, column) => (
          <div key={column} className="flex min-w-0 flex-1 flex-col gap-4">
            {props.dimensions !== false && (
              <div className="flex items-center gap-2 px-3">
                <Skeleton className="size-6 shrink-0 rounded-full" />
                <Skeleton className="h-3 w-16" />
              </div>
            )}
            <ul className="flex flex-col gap-1.5">
              {Array.from({ length: ROWS }, (_row, row) => (
                <IndicatorRowSkeleton key={row} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  )
}

/** The first step's footer: no way back yet, and the forward button's box around its label. */
export function DescribeFooterSkeleton() {
  const copy = useDescribeCopy()
  return (
    <div className="flex shrink-0 items-center justify-between gap-3 px-6 py-4">
      <div />
      <ButtonSkeleton label={copy.continueToIrrelevant} iconEnd />
    </div>
  )
}

/**
 * The first step's instructions, with the reference note as a skeleton the size of the likelier one: which note
 * applies depends on whether the job was described before.
 */
export function DescribeDescription(props: { indicator: Indicator; referenceNote: ReactNode }) {
  const copy = useDescribeCopy()
  return (
    <>
      {copy.stepDescriptions[props.indicator].relevant}
      {'\n'}
      {props.referenceNote}
    </>
  )
}

export function ReferenceNoteSkeleton() {
  return <TextSkeleton>{useDescribeCopy().marketReference}</TextSkeleton>
}

/** The describe route's pendingComponent: the wizard's first step, before its chunk has arrived. */
export function DescribeDialogPending(props: { indicator?: Indicator }) {
  const copy = useDescribeCopy()
  const { close } = useModalSlotPending()
  const full = props.indicator === undefined
  const indicator = props.indicator ?? 'competences'
  return (
    <DialogSkeleton
      label={full ? copy.fullTitle : copy.stepTitles[indicator].relevant}
      className={DESCRIBE_DIALOG_CLASS}
      frameClassName={DESCRIBE_FRAME_CLASS}
      showCloseButton={false}
    >
      <DescribeLayout
        close={
          <Button variant="ghost" size="icon-sm" className={DESCRIBE_CLOSE_CLASS} onClick={close}>
            <XIcon />
            <span className="sr-only">Close</span>
          </Button>
        }
        title={full && <h2 className={cn(DESCRIBE_FULL_TITLE_CLASS, 'font-medium')}>{copy.fullTitle}</h2>}
        steps={<StepsSkeleton count={full ? 9 : 3} indicator={indicator} full={full} />}
        heading={
          full ? (
            <h3 className={DESCRIBE_STEP_TITLE_CLASS}>{copy.stepTitles[indicator].relevant}</h3>
          ) : (
            <h2 className={cn(DESCRIBE_SINGLE_TITLE_CLASS, 'font-medium')}>
              <TextSkeleton>{copy.singleTitle(indicator, 'relevant', false)}</TextSkeleton>
            </h2>
          )
        }
        jobLine={<Skeleton className="inline-block h-[1em] w-48 align-middle" />}
        description={<DescribeDescription indicator={indicator} referenceNote={<ReferenceNoteSkeleton />} />}
      >
        <DescribeBody grid={<DescribeGridSkeleton />} footer={<DescribeFooterSkeleton />} />
      </DescribeLayout>
    </DialogSkeleton>
  )
}
